import type { ArbiterState } from '../state/store';

/** Snapshot of the controller's wait-state, sampled once per watchdog tick. */
export interface WatchdogSnapshot {
  arbiterState: ArbiterState;
  /** Wall time the arbiter state last changed. */
  stateSinceTs: number;
  hasPending: boolean;
  pendingTimerArmed: boolean;
  userSpeaking: boolean;
  sttInflight: number;
  eotInflight: number;
  hasActiveTurn: boolean;
  arbiterActive: boolean;
  interruptPhase: 'capturing' | 'transcribing' | 'grace' | null;
  /** Last confirmed speech inside the interrupt capture (null when no interrupt is open). */
  interruptLastEnergeticTs: number | null;
  interruptEndpointArmed: boolean;
}

export type WatchdogAction = 'none' | 'rearm-commit' | 'to-listening' | 'force-endpoint' | 'force-clear-turn';

export const WATCHDOG_LIMITS = {
  /** `listening` with a pending utterance but no commit timer and nothing in flight. */
  pendingNoTimerMs: 4_000,
  /** `thinking` with no active turn and nothing in flight. */
  thinkingIdleMs: 5_000,
  /** `thinking` with an active turn past the server LLM deadline — clear orphan wait-state. */
  thinkingActiveMs: 130_000,
  /** Arbiter sub-state shown while the arbiter itself is idle. */
  arbiterGhostMs: 2_000,
  /** Interrupt capture quiet for this long past the normal endpoint without emitting one. */
  interruptQuietMs: 650 + 1_500,
} as const;

const ARBITER_SUBSTATES: ReadonlySet<ArbiterState> = new Set(['candidate', 'ducked', 'paused', 'classifying']);

/**
 * Decide whether a wait-state has hung. Pure so it can be unit-tested; the controller applies
 * the returned action. Order matters: interrupt recovery first (it owns the mic), then pending,
 * then ghost UI states.
 */
export function decideWatchdogAction(s: WatchdogSnapshot, now: number): WatchdogAction {
  if (s.interruptPhase === 'capturing') {
    if (
      s.interruptEndpointArmed &&
      s.interruptLastEnergeticTs !== null &&
      now - s.interruptLastEnergeticTs > WATCHDOG_LIMITS.interruptQuietMs
    ) {
      return 'force-endpoint';
    }
    return 'none';
  }
  if (s.interruptPhase !== null) return 'none';

  const busy = s.userSpeaking || s.sttInflight > 0 || s.eotInflight > 0;
  const sinceState = now - s.stateSinceTs;

  if (s.hasPending && !s.pendingTimerArmed && !busy && sinceState > WATCHDOG_LIMITS.pendingNoTimerMs) {
    return 'rearm-commit';
  }
  if (
    s.arbiterState === 'thinking' &&
    s.hasActiveTurn &&
    sinceState > WATCHDOG_LIMITS.thinkingActiveMs
  ) {
    return 'force-clear-turn';
  }
  if (s.arbiterState === 'thinking' && !s.hasActiveTurn && !s.hasPending && !busy && sinceState > WATCHDOG_LIMITS.thinkingIdleMs) {
    return 'to-listening';
  }
  if (ARBITER_SUBSTATES.has(s.arbiterState) && !s.arbiterActive && !s.hasActiveTurn && sinceState > WATCHDOG_LIMITS.arbiterGhostMs) {
    return 'to-listening';
  }
  return 'none';
}
